import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/Layout/AppLayout';
import RequireAuth from '../components/RequireAuth';

// Lazy load components
const Landing = React.lazy(() => import('../pages/Landing'));
const Login = React.lazy(() => import('../pages/Login'));
const Register = React.lazy(() => import('../pages/Register'));
const PasswordReset = React.lazy(() => import('../pages/PasswordReset'));
const Dashboard = React.lazy(() => import('../pages/Dashboard'));
const Profile = React.lazy(() => import('../pages/Profile'));
const Children = React.lazy(() => import('../pages/Children'));
const Activities = React.lazy(() => import('../pages/Activities'));
const Calendar = React.lazy(() => import('../pages/Calendar'));
const FullScreenCalendar = React.lazy(() => import('../pages/FullScreenCalendar'));
const Settings = React.lazy(() => import('../pages/Settings'));
const NotificationSettings = React.lazy(() => import('../pages/NotificationSettings'));
const CalendarIntegration = React.lazy(() => import('../pages/CalendarIntegration'));
const About = React.lazy(() => import('../pages/About'));
const Terms = React.lazy(() => import('../pages/Terms'));
const Privacy = React.lazy(() => import('../pages/Privacy'));
const PremiumUpgrade = React.lazy(() => import('../pages/PremiumUpgrade'));
const PaymentHistory = React.lazy(() => import('../pages/PaymentHistory'));
const AcceptInvitation = React.lazy(() => import('../pages/AcceptInvitation'));
const CoParentManagement = React.lazy(() => import('../pages/CoParentManagement'));
const Chores = React.lazy(() => import('../pages/Chores'));
const Tasks = React.lazy(() => import('../pages/Tasks'));
const Grocery = React.lazy(() => import('../pages/Grocery'));
const DatabaseDebug = React.lazy(() => import('../pages/DatabaseDebug'));
const DataRecoveryTool = React.lazy(() => import('../debug/DataRecoveryTool'));
const DatabaseDebugTool = React.lazy(() => import('../debug/DatabaseDebugTool'));
const SafariDebugTool = React.lazy(() => import('../debug/SafariDebugTool'));
const ChildrenPageDiagnostic = React.lazy(() => import('../debug/ChildrenPageDiagnostic'));
const SystemAdmin = React.lazy(() => import('../pages/SystemAdmin'));
const AdminTools = React.lazy(() => import('../pages/AdminTools'));
const AdminDebug = React.lazy(() => import('../pages/AdminDebug'));
const AdminPortal = React.lazy(() => import('../pages/AdminPortal'));
const PremiumSuccess = React.lazy(() => import('../pages/PremiumSuccess'));

// OAuth Callback Components
const GoogleCalendarCallback = React.lazy(() => import('../components/CalendarIntegrationCallbacks').then(module => ({ default: module.GoogleCalendarCallback })));
const MicrosoftCalendarCallback = React.lazy(() => import('../components/CalendarIntegrationCallbacks').then(module => ({ default: module.MicrosoftCalendarCallback })));

// Add props interface for AppRoutes to accept the debug component
interface AppRoutesProps {
  debugComponent?: React.ComponentType | null;
}

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  const fallback = (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh', 
      fontSize: '1.5rem' 
    }}>
      Authenticating...
    </div>
  );

  if (loading) {
    return fallback;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public route - redirects to dashboard if already logged in
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC<AppRoutesProps> = ({ debugComponent: DebugComponent }) => {
  // Optimized loading fallback
  const fallback = (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      backgroundColor: '#f5f5f5',
      gap: '16px'
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        border: '3px solid #e0e0e0',
        borderTop: '3px solid #06B6D4',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <div style={{ 
        fontSize: '14px',
        color: '#666',
        fontFamily: '"Inter", "SF Pro", "Poppins", sans-serif'
      }}>
        Loading...
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return (
    <React.Suspense fallback={fallback}>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <Landing />
            </PublicRoute>
          } 
        />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } 
        />
        <Route 
          path="/password-reset" 
          element={
            <PublicRoute>
              <PasswordReset />
            </PublicRoute>
          } 
        />
        
        {/* Public Information Pages */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/about" element={<About />} />

        {/* OAuth Callback Routes - Public */}
        <Route path="/auth/google/callback" element={<GoogleCalendarCallback />} />
        <Route path="/auth/microsoft/callback" element={<MicrosoftCalendarCallback />} />

        {/* Protected Routes with AppLayout */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/children" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Children />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/activities" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Activities />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/calendar" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Calendar />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/calendar/fullscreen" 
          element={
            <ProtectedRoute>
              <FullScreenCalendar />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/chores" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Chores />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tasks" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Tasks />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/grocery" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Grocery />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <Settings />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/notification-settings" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <NotificationSettings />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/calendar-integration" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <CalendarIntegration />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/premium-upgrade" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <PremiumUpgrade />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/premium-success" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <PremiumSuccess />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-history" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <PaymentHistory />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/accept-invitation/:token" 
          element={
            <React.Suspense fallback={fallback}>
              <AcceptInvitation />
            </React.Suspense>
          } 
        />
        <Route 
          path="/co-parent-management" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <CoParentManagement />
              </AppLayout>
            </ProtectedRoute>
          } 
        />

        {/* Add Database Debug Route */}
        <Route 
          path="/debug-database" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <DatabaseDebug />
              </AppLayout>
            </ProtectedRoute>
          } 
        />

        {/* Add Data Recovery Tool Route */}
        <Route 
          path="/data-recovery" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <DataRecoveryTool />
              </AppLayout>
            </ProtectedRoute>
          } 
        />

        {/* Add Database Debug Tool Route */}
        <Route 
          path="/debug-database-tool" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <DatabaseDebugTool />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Safari Debug Tool Route - Public access for troubleshooting */}
        <Route
          path="/debug-safari"
          element={<SafariDebugTool />}
        />

        {/* Children Page Diagnostic Tool */}
        <Route
          path="/debug-children"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChildrenPageDiagnostic />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* System Admin Route - Should be protected by admin role in a real app */}
        <Route 
          path="/system-admin" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <SystemAdmin />
              </AppLayout>
            </ProtectedRoute>
          } 
        />

        {/* Admin Tools Route - Redirect to Admin Portal */}
        <Route 
          path="/admin-tools" 
          element={<Navigate to="/admin-portal" replace />}
        />

        {/* Admin Debug Route - Less restrictive for troubleshooting */}
        <Route 
          path="/admin-debug" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <AdminDebug />
              </AppLayout>
            </ProtectedRoute>
          } 
        />

        {/* Admin Portal Route - Comprehensive administration portal */}
        <Route
          path="/admin-portal"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AdminPortal />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin shortcut - Redirect to Admin Portal */}
        <Route
          path="/admin"
          element={<Navigate to="/admin-portal" replace />}
        />

        {/* Debug Route */}
        {DebugComponent && (
          <Route 
            path="/debug-db" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DebugComponent />
                </AppLayout>
              </ProtectedRoute>
            } 
          />
        )}

        {/* Fallback for all other routes */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </React.Suspense>
  );
};

export default AppRoutes; 